// src/components/PhoneCapture/usePhoneCaptureSession.js
//
// Desktop side of "Capture from Phone". Opens a capture session, exposes the URL
// to render as a QR, and reports files as the phone uploads them.
//
// Delivery is websocket-first (a transient `capture_files` control push from the
// backend) with a slow poll as a safety net, because a branch desktop that has
// lost its socket must still see the photos land.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCaptureSession,
  getCaptureSession,
  closeCaptureSession,
  captureUrlFor,
} from '../../services/captureApi';
import notificationStream from '../../modules/notifications/stream';

const POLL_INTERVAL_MS = 3000;

/**
 * @param {Object} config              target descriptor (see createCaptureSession)
 * @param {Function} onFiles           called with each batch of newly arrived files
 * @returns {{ open: Function, close: Function, session, url, files, loading, error, active }}
 */
export default function usePhoneCaptureSession(config, onFiles) {
  const [session, setSession] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs so the socket listener and poller never close over stale state.
  const tokenRef = useRef(null);
  const seenRef = useRef(new Set());
  const onFilesRef = useRef(onFiles);
  const pollRef = useRef(null);

  useEffect(() => { onFilesRef.current = onFiles; }, [onFiles]);

  // Dedupe by fileId: the socket push and the poll can both deliver the same
  // file, and a retry can deliver it twice.
  const ingest = useCallback((incoming = []) => {
    const fresh = incoming.filter((f) => f?.fileId && !seenRef.current.has(f.fileId));
    if (fresh.length === 0) return;
    fresh.forEach((f) => seenRef.current.add(f.fileId));
    setFiles((prev) => [...prev, ...fresh]);
    onFilesRef.current?.(fresh);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const open = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFiles([]);
    seenRef.current = new Set();
    try {
      const created = await createCaptureSession(config);
      tokenRef.current = created.token;
      setSession(created);
      return created;
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not start a phone capture session';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [config]);

  const close = useCallback(async () => {
    const token = tokenRef.current;
    stopPolling();
    tokenRef.current = null;
    setSession(null);
    if (token) {
      // Closing is a courtesy — the session expires on its own regardless.
      try { await closeCaptureSession(token); } catch { /* non-fatal */ }
    }
  }, [stopPolling]);

  const refresh = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const latest = await getCaptureSession(token);
      setSession(latest);
      ingest(latest.files || []);
    } catch {
      // Session may have expired or been reaped — the modal shows its own state.
    }
  }, [ingest]);

  // Live signal from the backend when the phone uploads. This rides the shared
  // realtime channel rather than push: the handshake needs sub-second in-tab
  // delivery and must not depend on browser notification permission.
  useEffect(() => {
    if (!session?.token) return undefined;
    const unsubscribe = notificationStream.subscribe(({ event, payload }) => {
      if (event !== 'capture.files') return;
      if (payload?.token !== tokenRef.current) return;
      const files = payload.files || [];
      ingest(files);
      // A delete on the phone pushes an empty delta; reconcile via poll.
      if (files.length === 0 && !payload.closed) refresh();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token, ingest, refresh]);

  // Polling fallback for a dropped socket.
  useEffect(() => {
    if (!session?.token) {
      stopPolling();
      return undefined;
    }
    pollRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return stopPolling;
  }, [session?.token, refresh, stopPolling]);

  // Never leave a session open behind a closed modal / unmounted form.
  useEffect(() => () => {
    stopPolling();
    const token = tokenRef.current;
    if (token) closeCaptureSession(token).catch(() => {});
  }, [stopPolling]);

  return {
    open,
    close,
    refresh,
    session,
    url: session?.token ? captureUrlFor(session.token) : null,
    files,
    loading,
    error,
    active: Boolean(session?.token),
  };
}
